import axios from 'axios';
import { API_CONFIG } from '../config/env';

export type FiatOnrampApprovalStatus = 'NotRequested' | 'Pending' | 'Approved' | 'Rejected' | 'Disabled';
export type FiatOnrampApprovalQueueFilter = FiatOnrampApprovalStatus | 'All';
export type MerchantRole = 'Admin' | 'SuperAgent' | 'Agent';

export interface FiatOnrampApprovalStatusInfo {
  merchantUserId: number;
  fiatOnrampEnabled: boolean;
  fiatOnrampApprovalStatus: FiatOnrampApprovalStatus;
  fiatOnrampApprovalTelegramUsername: string | null;
  fiatOnrampApprovalRequestedAtUtc: string | null;
  fiatOnrampApprovalReviewedAtUtc: string | null;
  fiatOnrampApprovalReviewedByUserId: number | null;
  fiatOnrampApprovalReviewNote: string | null;
}

export interface FiatOnrampApprovalQueueItem extends FiatOnrampApprovalStatusInfo {
  merchantName: string;
  merchantEmail: string;
  merchantRole: MerchantRole;
  merchantCreatedAtUtc: string | null;
}

const validStatuses: FiatOnrampApprovalStatus[] = [
  'NotRequested',
  'Pending',
  'Approved',
  'Rejected',
  'Disabled',
];

class FiatOnrampApprovalService {
  private getAuthHeaders() {
    const token = localStorage.getItem('accessToken');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  private normalizeStatus(status: unknown): FiatOnrampApprovalStatus {
    return typeof status === 'string' && validStatuses.includes(status as FiatOnrampApprovalStatus)
      ? status as FiatOnrampApprovalStatus
      : 'NotRequested';
  }

  private normalizeMerchantRole(role: unknown): MerchantRole {
    if (role === 'Admin' || role === 'SuperAgent' || role === 'Agent') {
      return role;
    }

    return 'Agent';
  }

  private normalizeStatusInfo(raw: unknown): FiatOnrampApprovalStatusInfo {
    const source = (raw && typeof raw === 'object' ? raw as Record<string, unknown> : {});

    return {
      merchantUserId: Number(source.merchantUserId) || 0,
      fiatOnrampEnabled: Boolean(source.fiatOnrampEnabled),
      fiatOnrampApprovalStatus: this.normalizeStatus(source.fiatOnrampApprovalStatus),
      fiatOnrampApprovalTelegramUsername: typeof source.fiatOnrampApprovalTelegramUsername === 'string'
        ? source.fiatOnrampApprovalTelegramUsername.trim() || null
        : null,
      fiatOnrampApprovalRequestedAtUtc: typeof source.fiatOnrampApprovalRequestedAtUtc === 'string'
        ? source.fiatOnrampApprovalRequestedAtUtc.trim() || null
        : null,
      fiatOnrampApprovalReviewedAtUtc: typeof source.fiatOnrampApprovalReviewedAtUtc === 'string'
        ? source.fiatOnrampApprovalReviewedAtUtc.trim() || null
        : null,
      fiatOnrampApprovalReviewedByUserId: typeof source.fiatOnrampApprovalReviewedByUserId === 'number'
        ? source.fiatOnrampApprovalReviewedByUserId
        : null,
      fiatOnrampApprovalReviewNote: typeof source.fiatOnrampApprovalReviewNote === 'string'
        ? source.fiatOnrampApprovalReviewNote.trim() || null
        : null,
    };
  }

  private normalizeQueueItem(raw: unknown): FiatOnrampApprovalQueueItem {
    const source = (raw && typeof raw === 'object' ? raw as Record<string, unknown> : {});
    const statusInfo = this.normalizeStatusInfo(raw);

    return {
      ...statusInfo,
      merchantName: typeof source.merchantName === 'string' ? source.merchantName.trim() : '',
      merchantEmail: typeof source.merchantEmail === 'string' ? source.merchantEmail.trim() : '',
      merchantRole: this.normalizeMerchantRole(source.merchantRole),
      merchantCreatedAtUtc: typeof source.merchantCreatedAtUtc === 'string'
        ? source.merchantCreatedAtUtc.trim() || null
        : null,
    };
  }

  async getMyStatus(): Promise<FiatOnrampApprovalStatusInfo> {
    const response = await axios.get(`${API_CONFIG.BASE_URL}/fiat-onramp-approvals/me`, {
      headers: this.getAuthHeaders(),
    });

    return this.normalizeStatusInfo(response.data);
  }

  async submitMyRequest(telegramUsername: string): Promise<FiatOnrampApprovalStatusInfo> {
    const response = await axios.post(
      `${API_CONFIG.BASE_URL}/fiat-onramp-approvals/me/request`,
      {
        telegramUsername,
      },
      {
        headers: this.getAuthHeaders(),
      }
    );

    return this.normalizeStatusInfo(response.data);
  }

  async getRequests(status: FiatOnrampApprovalStatus | null = null): Promise<FiatOnrampApprovalQueueItem[]> {
    const response = await axios.get(`${API_CONFIG.BASE_URL}/fiat-onramp-approvals/requests`, {
      headers: this.getAuthHeaders(),
      params: status ? { status } : {},
    });

    const items = Array.isArray(response.data) ? response.data : [];
    return items.map((item) => this.normalizeQueueItem(item));
  }

  async setMerchantState(
    merchantUserId: number,
    enabled: boolean,
    reviewNote: string | null = null
  ): Promise<FiatOnrampApprovalStatusInfo> {
    const response = await axios.post(
      `${API_CONFIG.BASE_URL}/fiat-onramp-approvals/requests/${merchantUserId}/state`,
      {
        enabled,
        reviewNote: reviewNote?.trim() || null,
      },
      {
        headers: this.getAuthHeaders(),
      }
    );

    return this.normalizeStatusInfo(response.data);
  }
}

export const fiatOnrampApprovalService = new FiatOnrampApprovalService();
